import IconCommonStyle from 'app/components/Icons/IconCommonStyle';
import { EMenu } from 'app/enum';
import { EPath } from 'app/routes/routesConfig';
import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import UnreadIcon from '../../../assets/icon/unread.svg';
import useService from '../Chat/service';
import { getUserFromLocalStorage } from '../../../helpers/localStorage';
import packageJson from '../../../../package.json';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from 'services/firebase';
import { axiosClient } from 'api/axiosClient';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ContactEmergencyOutlinedIcon from '@mui/icons-material/ContactEmergencyOutlined';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import { Tooltip } from 'antd';

export const sendEmailForConsultant = async (msg: string, email: string) => {
  await axiosClient.post('public/user/send-msg-email', {
    msg,
    email,
  });
};

interface Props {
  collapsed?: boolean;
  onToggle?: () => void;
}

interface MenuItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
  onClick?: () => void;
  showBadge?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ to, icon, label, collapsed, onClick, showBadge }) => (
  <NavLink to={to} onClick={onClick}>
    <Tooltip title={collapsed ? label : ''} placement="right">
      <div
        className={`text-lg/[22px] font-normal text-text p-4 border-b border-light-gray flex items-center ${
          collapsed ? 'justify-center' : 'gap-2'
        } ${!collapsed && showBadge ? 'justify-between' : ''}`}
      >
        <div className={`flex items-center ${collapsed ? '' : 'gap-2'}`}>
          {icon}
          {!collapsed && <span>{label}</span>}
        </div>
        {!collapsed && showBadge && (
          <img src={UnreadIcon} alt="" className="w-3 h-3 my-auto animate-bounce" />
        )}
      </div>
    </Tooltip>
  </NavLink>
);

const SideBar: React.FC<Props> = ({ collapsed, onToggle }) => {
  const currentUser = getUserFromLocalStorage();
  const { checkUnread, listDataGroup, listDirectChat, currentUserChat, checkGroupWithCom } =
    useService();

  useEffect(() => {
    checkGroupWithCom();
  }, [currentUserChat]);

  useEffect(() => {
    if (Object.keys(currentUserChat).length && !currentUserChat?.role) {
      const docRef = doc(db, 'user', currentUserChat?.uid);
      updateDoc(docRef, {
        role: getUserFromLocalStorage()?.role ?? 'unknown',
      });
    }
    localStorage.setItem('currentUid', currentUserChat?.uid);
  }, [currentUserChat]);

  const isManager = (currentUser.employee && currentUser.employee.isManager) || currentUser.manager;

  const hasUnreadMessages =
    listDataGroup.filter((group: any) => checkUnread(group.uid)).length > 0 ||
    listDirectChat.filter((direct: any) => checkUnread(direct.uid)).length > 0 ||
    listDataGroup
      .map((x) => x?.membersUnread?.includes(currentUserChat.uid))
      .filter((x) => x)
      .includes(true);

  return (
    <div
      className={`flex flex-col w-full bg-white shadow-16x-right h-[calc(100vh-48px)] relative transition-all duration-300`}
    >
      <div className="flex-1 overflow-y-auto custom-scroll">
        <div className="flex flex-col bg-white">
          {!currentUser.consultant && (
            <MenuItem
              to={EPath.implements}
              icon={<EventIcon />}
              label={EMenu.Implementation_times_list}
              collapsed={collapsed}
              onClick={() => localStorage.removeItem('my-infinite-scroll-position')}
            />
          )}

          {currentUser.consultant && (
            <MenuItem
              to={EPath.interviews}
              icon={<CalendarMonthIcon />}
              label={EMenu.myPage}
              collapsed={collapsed}
            />
          )}

          {!currentUser.consultant && (
            <MenuItem
              to={EPath.surveyList}
              icon={<CalendarMonthIcon />}
              label={EMenu.survey_list}
              collapsed={collapsed}
            />
          )}

          {!currentUser.consultant && (
            <MenuItem
              to={EPath.additionalInterview}
              icon={<PermContactCalendarOutlinedIcon />}
              label={EMenu.Additional_interview_list}
              collapsed={collapsed}
              onClick={() => localStorage.removeItem('my-infinite-scroll-position')}
            />
          )}

          {(currentUser.consultant || currentUser.com) && (
            <MenuItem
              to={EPath.message}
              icon={<ChatBubbleOutlineIcon />}
              label={EMenu.message}
              collapsed={collapsed}
              showBadge={hasUnreadMessages}
            />
          )}

          {currentUser.com && (
            <MenuItem
              to={EPath.interviews}
              icon={<ListAltIcon />}
              label={EMenu.list_interview}
              collapsed={collapsed}
            />
          )}

          {currentUser.consultant && (
            <>
              <MenuItem
                to={EPath.Interview_Management}
                icon={<ListAltIcon />}
                label={EMenu.interview_management}
                collapsed={collapsed}
              />
              <NavLink
                to={'https://www.notion.so/1403293d56b849b3aba0260dd69f9e07?pvs=4'}
                target="_blank"
              >
                <Tooltip title={collapsed ? EMenu.interview_manual : ''} placement="right">
                  <div
                    className={`flex text-lg/[22px] font-normal text-text p-4 border-b border-light-gray items-center ${
                      collapsed ? 'justify-center' : 'gap-2'
                    } ${!collapsed ? 'justify-between' : ''}`}
                  >
                    <div className={`flex items-center ${collapsed ? '' : 'gap-2'}`}>
                      <BookOutlinedIcon />
                      {!collapsed && <p>{EMenu.interview_manual}</p>}
                    </div>
                    {!collapsed && (
                      <IconCommonStyle name="link-out" size={20} className="my-auto" />
                    )}
                  </div>
                </Tooltip>
              </NavLink>
            </>
          )}
        </div>

        {!currentUser.consultant && (
          <div className="flex flex-col bg-white border-light-gray">
            {/* {!isManager && (
              <>
                <MenuItem
                  to={EPath.analyzeOmoteraDiagnosisResult}
                  icon={<ListAltIcon />}
                  label={EMenu['analyze_omoteura-result']}
                  collapsed={collapsed}
                />
                <MenuItem
                  to={EPath.comparisonAxis}
                  icon={<ListAltIcon />}
                  label={EMenu['face-back_diagnostic_analysis']}
                  collapsed={collapsed}
                />
              </>
            )} */}
            <MenuItem
              to={EPath.Client_ID_Management}
              icon={<PersonPinOutlinedIcon />}
              label={EMenu.client_ID_management}
              collapsed={collapsed}
            />
            {!isManager && (
              <>
                <MenuItem
                  to={EPath.Interviewer_ID_Management}
                  icon={<PersonOutlineIcon />}
                  label={EMenu.interviewer_ID_Management}
                  collapsed={collapsed}
                />
                <MenuItem
                  to={EPath.Com_ID_Management}
                  icon={<ContactEmergencyOutlinedIcon />}
                  label={EMenu['.com_ID_Management']}
                  collapsed={collapsed}
                />
                {/* <MenuItem
                  to={EPath.CareerMemoRegister}
                  icon={<ListAltIcon />}
                  label={EMenu.career_memo_management}
                  collapsed={collapsed}
                /> */}
              </>
            )}
          </div>
        )}

        {currentUser.com && (
          <div className="flex flex-col bg-white border-light-gray">
            <MenuItem
              to={EPath.Data_download}
              icon={<DownloadForOfflineOutlinedIcon />}
              label={EMenu.Data_download}
              collapsed={collapsed}
            />
          </div>
        )}
      </div>
      <div
        className={`flex items-center w-full gap-2 border-t border-light-gray bg-white px-4 py-3 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!collapsed && (
          <span className="text-sm font-medium text-text">{`v${packageJson.version}`}</span>
        )}

        <span onClick={onToggle} className="cursor-pointer">
          <IconCommonStyle name={collapsed ? 'group_right_arrow' : 'group_left_arrow'} size={20} />
        </span>
      </div>
    </div>
  );
};

export default SideBar;
